import { ArrowUpRight, Instagram } from "lucide-react";
import { Eyebrow, Reveal, scrollToId } from "./motion";
import { startCheckout } from "./payments";

const BOOK_IMG =
    "https://images.unsplash.com/photo-1761718210089-ba3bb5ccb54f?q=80&w=1400&auto=format&fit=crop";
const MAILTO =
    "mailto:hello@inskinau.com.au?subject=Consultation%20request%20%E2%80%94%20In%20Skin";

const CONTACTS = [
    {
        label: "Instagram",
        value: "@inskinau",
        href: "https://instagram.com/inskinau",
        external: true,
        testid: "booking-instagram-link",
    },
    {
        label: "Email",
        value: "hello@inskinau.com.au",
        href: MAILTO,
        external: false,
        testid: "booking-email-link",
    },
    {
        label: "Studio",
        value: "Australia — by appointment",
        href: null,
        testid: "booking-location-text",
    },
];

export default function Booking() {
    return (
        <section
            id="book"
            data-testid="booking-section"
            className="relative overflow-hidden bg-ink px-6 py-24 text-cream md:px-12 md:py-36"
        >
            {/* Watermark */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-8 right-0 select-none font-serif text-[22vw] italic leading-none text-cream/[0.04]"
            >
                In Skin
            </span>

            <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 md:grid-cols-12">
                <div className="md:col-span-7">
                    <Eyebrow index="05" light>
                        Begin
                    </Eyebrow>

                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-serif text-[clamp(2.6rem,6vw,6rem)] font-light leading-[1.02]">
                            Ready when
                            <br />
                            <span className="italic text-sand">you are.</span>
                        </h2>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-md text-base font-light leading-relaxed text-cream/70 md:text-lg">
                            Consultations are one-on-one and unhurried. Write to
                            us about your skin — we will take it from there.
                        </p>
                    </Reveal>

                    <Reveal delay={0.3}>
                        <div className="mt-10 flex flex-wrap items-center gap-6">
                            <a
                                data-testid="booking-cta-book-button"
                                href={MAILTO}
                                className="rounded-full bg-cream px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-sand hover:shadow-2xl"
                            >
                                Book online
                            </a>
                            <button
                                data-testid="booking-pay-deposit-button"
                                onClick={() => startCheckout("booking_deposit")}
                                className="rounded-full border border-cream/30 px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream/10 hover:shadow-xl"
                            >
                                Pay $25 deposit
                            </button>
                            <button
                                data-testid="booking-cta-store-button"
                                onClick={() => scrollToId("store")}
                                className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-sand transition-colors duration-300 hover:text-cream"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/25">
                                    <ArrowUpRight size={15} strokeWidth={1.5} />
                                </span>
                                Explore our store
                            </button>
                        </div>
                        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/50">
                            Secure checkout via Stripe · Deposit balance payable in studio
                        </p>
                    </Reveal>

                    <Reveal delay={0.4}>
                        <div className="mt-16 space-y-5 border-t border-cream/10 pt-10">
                            {CONTACTS.map((c) => (
                                <div
                                    key={c.label}
                                    className="flex flex-wrap items-baseline justify-between gap-2 md:max-w-md"
                                >
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                                        {c.label}
                                    </span>
                                    {c.href ? (
                                        <a
                                            data-testid={c.testid}
                                            href={c.href}
                                            target={c.external ? "_blank" : undefined}
                                            rel={c.external ? "noreferrer" : undefined}
                                            className="link-underline flex items-center gap-2 font-serif text-xl font-light italic text-cream"
                                        >
                                            {c.label === "Instagram" && <Instagram size={15} />}
                                            {c.value}
                                            {c.external && <ArrowUpRight size={14} strokeWidth={1.5} />}
                                        </a>
                                    ) : (
                                        <span
                                            data-testid={c.testid}
                                            className="font-serif text-xl font-light italic text-cream"
                                        >
                                            {c.value}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <div className="md:col-span-5">
                    <Reveal delay={0.25}>
                        <div className="overflow-hidden rounded-[28px] border border-cream/10">
                            <img
                                src={BOOK_IMG}
                                alt="A facial treatment underway at the In Skin studio"
                                loading="lazy"
                                className="h-[380px] w-full object-cover md:h-[540px]"
                            />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
